import { useState, useEffect, useRef } from 'react';
import { Mic, Play, Pause, SkipForward, SkipBack, Volume2, Heart, Share2, Upload, Youtube, Music, Users, Radio } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import MuziCoach from '../components/MuziCoach';
import { LIVE_TRACKS } from '../mock';

// Placeholder UI lines (synced demo) — sera remplacé en Phase 4 par paroles réelles via Musixmatch API
const LYRICS_DEMO = [
  { time: 0, text: '♪ Démarre la musique ♪' },
  { time: 4, text: '♪ Importe ton titre préféré ♪' },
  { time: 8, text: '♪ Active ta voix ♪' },
  { time: 12, text: '♪ Le coach va réagir en live ♪' },
  { time: 16, text: '♪ Partage en un clic ♪' },
  { time: 20, text: '♪ Deviens viral ♪' },
];

export default function Live() {
  const [playing, setPlaying] = useState(false);
  const [currentLyric, setCurrentLyric] = useState(0);
  const [progress, setProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const [coachReaction, setCoachReaction] = useState(null);
  const [activeTrack, setActiveTrack] = useState(LIVE_TRACKS[0]);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress((p) => {
          const next = p + 1;
          const idx = Math.floor(next / 4) % LYRICS_DEMO.length;
          setCurrentLyric(idx);
          if (next % 8 === 0 && next > 0) {
            const reactions = ['C\'est FEU 🔥', 'WOW belle voix !', 'Continue comme ça !', 'YEAHHH ! 🎤', 'GROSSE perf !'];
            setCoachReaction(reactions[Math.floor(Math.random() * reactions.length)]);
          }
          return next > 50 ? 0 : next;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing]);

  const handleImport = () => {
    toast.success('🎵 Musique importée (démo)', { description: 'Format MP3, M4A, WAV supportés' });
  };
  const handleYoutube = () => {
    toast.success('📺 Lien YouTube chargé (démo)');
  };

  return (
    <div className="relative pt-24 pb-12 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <Badge className="bg-blue-500/15 text-blue-300 border-0 mb-2 px-3 py-1 rounded-full">
              <Mic size={12} className="mr-1" /> Karaoké Live
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-black">Lance la <span className="text-gradient">scène</span></h1>
            <p className="text-zinc-400 mt-2">Choisis ta musique, chante, ton coach réagit en live.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleImport} variant="outline" className="bg-white/5 hover:bg-white/10 text-white border-white/15 rounded-xl h-10 px-4 font-bold">
              <Upload size={16} className="mr-1.5" /> Importer
            </Button>
            <Button onClick={handleYoutube} variant="outline" className="bg-white/5 hover:bg-white/10 text-white border-white/15 rounded-xl h-10 px-4 font-bold">
              <Youtube size={16} className="mr-1.5" /> YouTube
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Player + Lyrics */}
          <div className="lg:col-span-2">
            <div className="glass rounded-3xl overflow-hidden">
              {/* Cover */}
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={activeTrack.cover} alt={activeTrack.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/40 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  {recording && (
                    <div className="flex items-center gap-1.5 bg-red-500/90 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-bold text-white">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                    </div>
                  )}
                  <div className="glass-light px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
                    <Users size={11} /> 23
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h2 className="text-2xl font-black text-white">{activeTrack.title}</h2>
                  <p className="text-sm text-zinc-300">{activeTrack.artist}</p>
                </div>
              </div>

              {/* Lyrics */}
              <div className="p-6 min-h-[160px] flex flex-col items-center justify-center text-center">
                {LYRICS_DEMO.map((l, i) => (
                  <div
                    key={`lyric-${l.time}`}
                    className={`transition-all duration-300 ${i === currentLyric ? 'text-3xl font-black text-gradient scale-110' : Math.abs(i - currentLyric) === 1 ? 'text-xl text-zinc-400' : 'hidden md:block text-sm text-zinc-600'}`}
                  >
                    {l.text}
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div className="p-6 border-t border-white/5">
                <Slider value={[progress]} max={50} className="mb-3" onValueChange={(v) => setProgress(v[0])} />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400 font-mono">0:{String(progress).padStart(2, '0')}</span>
                  <div className="flex items-center gap-2">
                    <button className="w-10 h-10 rounded-full glass-light flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                      <SkipBack size={18} />
                    </button>
                    <button
                      onClick={() => setPlaying(!playing)}
                      className="w-14 h-14 rounded-full btn-neon flex items-center justify-center text-white"
                    >
                      {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />}
                    </button>
                    <button className="w-10 h-10 rounded-full glass-light flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                      <SkipForward size={18} />
                    </button>
                  </div>
                  <span className="text-xs text-zinc-400 font-mono">{activeTrack.duration}</span>
                </div>

                <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/5">
                  <Button
                    onClick={() => { setRecording(!recording); toast.success(recording ? 'Live terminé' : '🔴 LIVE lancé !'); }}
                    className={`h-11 px-5 rounded-xl font-bold ${recording ? 'bg-red-500 hover:bg-red-600' : 'btn-neon'} text-white border-0`}
                  >
                    <Radio size={16} className="mr-1.5" /> {recording ? 'Arrêter Live' : 'Lancer Live'}
                  </Button>
                  <div className="flex gap-2">
                    <button className="w-10 h-10 rounded-xl glass-light flex items-center justify-center text-zinc-300 hover:text-pink-400 transition-colors">
                      <Heart size={18} />
                    </button>
                    <button onClick={() => toast.success('Lien copié !')} className="w-10 h-10 rounded-xl glass-light flex items-center justify-center text-zinc-300 hover:text-white transition-colors">
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Coach */}
            <div className="glass rounded-2xl p-5">
              <div className="text-sm font-bold text-white mb-3">Ton coach réagit</div>
              <MuziCoach coachId="c1" reaction={coachReaction} size="md" />
            </div>

            {/* Track list */}
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-white flex items-center gap-2"><Music size={16} className="text-pink-400" /> Playlist</h3>
                <span className="text-xs text-zinc-400">{LIVE_TRACKS.length} titres</span>
              </div>
              <div className="space-y-2">
                {LIVE_TRACKS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setActiveTrack(t); setProgress(0); setPlaying(true); }}
                    className={`w-full flex items-center gap-3 p-2 rounded-xl transition-colors text-left ${activeTrack.id === t.id ? 'bg-pink-500/15' : 'hover:bg-white/5'}`}
                  >
                    <img src={t.cover} alt={t.title} className="w-11 h-11 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white text-sm truncate">{t.title}</div>
                      <div className="text-xs text-zinc-400 truncate">{t.artist}</div>
                    </div>
                    <span className="text-xs text-zinc-500 font-mono">{t.duration}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Live viewers */}
            <div className="glass rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Users size={16} className="text-blue-400" /> Spectateurs</h3>
              <div className="flex -space-x-2 mb-3">
                {['👩‍🎤', '🎤', '💜', '⭐', '🎮', '⚡'].map((a) => (
                  <div key={`spec-${a}`} className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 border-2 border-[#0A0A0F] flex items-center justify-center text-base">
                    {a}
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full bg-white/10 border-2 border-[#0A0A0F] flex items-center justify-center text-[10px] text-white font-bold">
                  +17
                </div>
              </div>
              <div className="text-xs text-zinc-400">23 personnes écoutent ton live</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
